import { useEffect, useState } from 'react';
import { abortJob } from '../api';
import type { Job } from '../types';
import { AdminAction } from '../../components/PermissionGate';

interface LiveScansTableProps {
  jobs: Job[];
  onMutated?: () => void;
}

function formatTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function elapsedSeconds(startedAt: string | null, now: number): number | null {
  if (!startedAt) return null;
  const started = new Date(startedAt).getTime();
  return Math.max(0, Math.floor((now - started) / 1000));
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

function StatusPill({ status }: { status: Job['status'] }) {
  const styles =
    status === 'processing'
      ? 'bg-amber-100 text-amber-800'
      : status === 'pending'
      ? 'bg-primary-100 text-primary-800'
      : 'bg-primary-50 text-primary-600';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${styles}`}>
      {status === 'processing' && (
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
      )}
      {status === 'pending' && <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />}
      {status}
    </span>
  );
}

interface AbortButtonProps {
  job: Job;
  onMutated?: () => void;
}

function AbortButton({ job, onMutated }: AbortButtonProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    if (busy) return;
    const ok = window.confirm(
      `Abort scan ${job.scan_id}?\n\n` +
        `The job will be marked as failed. Note that fal.ai requests already in flight ` +
        `cannot be cancelled mid-call — the abort takes effect at the next checkpoint.`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await abortJob(job.job_id);
      onMutated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className="px-2.5 py-1 rounded-md text-xs font-medium border border-red-200 text-red-700 bg-white hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {busy ? 'Aborting…' : 'Abort'}
      </button>
      {error && <span className="text-[11px] text-red-600 max-w-[14rem] truncate" title={error}>{error}</span>}
    </div>
  );
}

export function LiveScansTable({ jobs, onMutated }: LiveScansTableProps) {
  const [now, setNow] = useState(() => Date.now());
  // Mobile cards collapse to Scan ID / Status by default; tapping a card
  // expands it. Keyed by job_id so expansion survives re-renders.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (jobId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (jobs.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-primary-200 bg-white px-6 py-10 text-center text-primary-500 text-sm">
        No live scans. Click <span className="font-semibold">Generate Scan</span> to start one.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-primary-100 bg-white shadow-card">
      <table className="data-table mobile-collapsible min-w-full text-sm">
        <thead className="bg-primary-50/60 text-primary-600 text-xs uppercase tracking-wider">
          <tr>
            <th className="px-4 py-3 text-left font-semibold">Scan ID</th>
            <th className="px-4 py-3 text-left font-semibold">Status</th>
            <th className="px-4 py-3 text-left font-semibold">Submitted</th>
            <th className="px-4 py-3 text-left font-semibold">Started</th>
            <th className="px-4 py-3 text-left font-semibold">Elapsed</th>
            <th className="px-4 py-3 text-right font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-primary-100">
          {jobs.map((job) => (
            <tr
              key={job.job_id}
              className="hover:bg-primary-50/40"
              data-collapsed={!expandedRows.has(job.job_id)}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('button, a, input, select')) return;
                toggleRow(job.job_id);
              }}
            >
              <td className="px-4 py-3 font-mono text-xs text-primary-900 break-all" data-label="Scan ID">{job.scan_id}</td>
              <td className="px-4 py-3" data-label="Status">
                <StatusPill status={job.status} />
              </td>
              <td className="px-4 py-3 text-primary-700" data-label="Submitted">{formatTime(job.created_at)}</td>
              <td className="px-4 py-3 text-primary-700" data-label="Started">{formatTime(job.started_at)}</td>
              <td className="px-4 py-3 text-primary-700 font-mono" data-label="Elapsed">
                {formatDuration(elapsedSeconds(job.started_at, now))}
              </td>
              <td className="px-4 py-3 text-right" data-label="Actions">
                <AdminAction>
                  <AbortButton job={job} onMutated={onMutated} />
                </AdminAction>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
